/**
 * Taste Preference Matching
 *
 * Matches taste profile preferences against recipe attributes to produce
 * scoring adjustments for the meal plan generation algorithm.
 *
 * Uses keyword extraction and sentiment detection (no AI/NLP — simple keyword matching).
 */

import { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import { TasteProfile } from '../redux/feedbackSlice';

export type PreferenceSentiment = 'positive' | 'negative' | 'neutral';

// Common English stop words to filter out of preference text
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'shall', 'can', 'need', 'must',
  'and', 'or', 'but', 'if', 'then', 'so', 'than', 'that', 'this',
  'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as',
  'into', 'about', 'between', 'through', 'after', 'before', 'above',
  'it', 'its', 'they', 'them', 'their', 'we', 'our', 'your', 'he',
  'she', 'his', 'her', 'my', 'me', 'us', 'i', 'you',
  'not', 'no', 'nor', 'very', 'too', 'also', 'just', 'more',
  'most', 'some', 'any', 'all', 'each', 'every', 'both',
  'up', 'out', 'off', 'over', 'under',
]);

const POSITIVE_MARKERS = [
  'love', 'loves', 'loving',
  'prefer', 'prefers', 'preferred', 'preference',
  'enjoy', 'enjoys', 'enjoying',
  'favorite', 'favourite', 'favorites', 'favourites',
  'like', 'likes',
  'appreciate', 'appreciates',
  'adore', 'adores',
];

const NEGATIVE_MARKERS = [
  'dislike', 'dislikes', 'disliking',
  'avoid', 'avoids', 'avoiding',
  'hate', 'hates', 'hating',
  'too much',
  'not enjoy', 'not like', 'not fond',
  'aversion',
  'reject', 'rejects',
];

/**
 * Extract meaningful keywords from a preference text string.
 * Filters out stop words and returns lowercase tokens.
 */
export function extractKeywords(text: string): string[] {
  if (!text.trim()) return [];

  return text
    .toLowerCase()
    .replace(/[.,!?;:()'"]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 0 && !STOP_WORDS.has(word));
}

/**
 * Detect sentiment from a preference key string.
 * Checks for positive/negative marker words to classify intent.
 */
export function detectPreferenceSentiment(key: string): PreferenceSentiment {
  const lower = key.toLowerCase();

  // Check negative first (more specific — "not like" should be negative, not positive)
  for (const marker of NEGATIVE_MARKERS) {
    if (lower.includes(marker)) return 'negative';
  }

  for (const marker of POSITIVE_MARKERS) {
    if (lower.includes(marker)) return 'positive';
  }

  return 'neutral';
}

/**
 * Match a single taste profile preference against a recipe.
 * Returns a score: positive for liked matches, negative for disliked matches, 0 for no match.
 *
 * @param preferenceKey - The preference category/key (e.g., "Loves Mediterranean cuisine")
 * @param preferenceValue - The preference description (e.g., "Mediterranean and Greek food is a family favorite")
 * @param recipe - The recipe to match against
 * @returns Score adjustment (-34 to +29 per preference)
 */
export function matchPreferenceToRecipe(
  preferenceKey: string,
  preferenceValue: string,
  recipe: PersonalRecipeIndexEntry
): number {
  // The digest writes its keys from a fixed list (proteins, cuisines,
  // weeknight_style…), and only `dislikes` of those carries a marker — so
  // reading the key alone scored every learned preference at zero and the
  // profile moved nothing. The sentiment is in the sentence the model wrote.
  const sentiment = detectPreferenceSentiment(`${preferenceKey} ${preferenceValue}`);

  // Neutral preferences don't affect scoring
  if (sentiment === 'neutral') return 0;

  // Extract keywords from both key and value
  const keyKeywords = extractKeywords(preferenceKey);
  const valueKeywords = extractKeywords(preferenceValue);
  const allKeywords = [...new Set([...keyKeywords, ...valueKeywords])];

  if (allKeywords.length === 0) return 0;

  // Build searchable text from recipe attributes
  const recipeTitle = recipe.title.toLowerCase();
  const recipeCuisines = (recipe.cuisines ?? []).map(c => c.toLowerCase());
  const recipeDishTypes = (recipe.dishTypes ?? []).map(d => d.toLowerCase());
  const recipeIngredients = (recipe.ingredients ?? []).map(i => i.toLowerCase());

  // Count keyword matches across recipe attributes
  let matchCount = 0;

  for (const keyword of allKeywords) {
    // Title match (strong signal)
    if (recipeTitle.includes(keyword)) {
      matchCount++;
    }

    // Cuisine match (strong signal)
    if (recipeCuisines.some(c => c.includes(keyword))) {
      matchCount++;
    }

    // Dish type match
    if (recipeDishTypes.some(d => d.includes(keyword))) {
      matchCount++;
    }

    // Ingredient match
    if (recipeIngredients.some(i => i.includes(keyword))) {
      matchCount++;
    }
  }

  if (matchCount === 0) return 0;

  // Scale score based on match count (more matches = stronger signal)
  // 1 match = base, 2+ matches = stronger
  const baseScore = sentiment === 'positive' ? 15 : -20;
  const matchBonus = Math.min(matchCount - 1, 2) * (sentiment === 'positive' ? 7 : -7);

  return baseScore + matchBonus;
}

// Scoring caps for taste profile bonus
const MAX_POSITIVE_BONUS = 50;
const MAX_NEGATIVE_PENALTY = -60;

/**
 * Calculate the aggregate taste profile bonus for a recipe.
 * Iterates over all preferences in the taste profile, matches each against
 * the recipe, and sums the adjustments with capping.
 *
 * @param recipe - The recipe to score
 * @param tasteProfile - The user's taste profile (null = no adjustment)
 * @returns Capped score adjustment in range [-60, +50]
 */
export function calculateTasteProfileBonus(
  recipe: PersonalRecipeIndexEntry,
  tasteProfile: TasteProfile | null
): number {
  if (!tasteProfile || !tasteProfile.preferences) return 0;

  // One answer per recipe object and profile (#865): the planner's rankings
  // ask about the whole library on every opening and on every echo while the
  // sheet is open, and this was 150 ms a pass on a throttled phone. The index
  // entries are long-lived objects; a new profile is a new answer.
  const hit = bonusCache.get(recipe);
  if (hit && hit.profile === tasteProfile) return hit.value;
  const value = computeTasteProfileBonus(recipe, tasteProfile);
  bonusCache.set(recipe, { profile: tasteProfile, value });
  return value;
}

const bonusCache = new WeakMap<object, { profile: TasteProfile; value: number }>();

function computeTasteProfileBonus(recipe: PersonalRecipeIndexEntry, tasteProfile: TasteProfile): number {
  const entries = Object.entries(tasteProfile.preferences);
  if (entries.length === 0) return 0;

  let totalBonus = 0;

  for (const [key, value] of entries) {
    totalBonus += matchPreferenceToRecipe(key, value, recipe);
  }

  // Cap within bounds
  return Math.max(MAX_NEGATIVE_PENALTY, Math.min(MAX_POSITIVE_BONUS, totalBonus));
}
